import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import GrainIcon from '@mui/icons-material/Grain';
import EggIcon from '@mui/icons-material/Egg';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SpeedIcon from '@mui/icons-material/Speed';
import ScaleIcon from '@mui/icons-material/Scale';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useFeedConversionRatio } from '../hooks/useFeed';

const BATCH_COLORS = ['#42a5f5', '#66bb6a', '#ab47bc', '#ef5350', '#26c6da', '#ffa726'];

const AXIS_STYLE = { fill: '#9e9e9e', fontSize: 11 };
const GRID_STYLE = { stroke: 'rgba(255,255,255,0.06)' };

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  sub?: string;
  color: string;
  lowerIsBetter?: boolean;
}

function StatCard({ icon, label, value, sub, color, lowerIsBetter }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderTop: `3px solid ${color}`,
        background: `linear-gradient(135deg, rgba(${hexToRgb(color)},0.08) 0%, transparent 60%)`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color }}>
        {icon}
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
        {value ?? '—'}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {sub}
        </Typography>
      )}
      {lowerIsBetter && value && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingDownIcon sx={{ fontSize: 14, color: '#66bb6a' }} />
          <Typography variant="caption" sx={{ color: '#66bb6a', fontSize: 10 }}>
            lower is better
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

function ChartSection({
  title,
  subtitle,
  children,
  height = 280,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  height?: number;
}) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
          {subtitle}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        {children as React.ReactElement}
      </ResponsiveContainer>
    </Paper>
  );
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, minWidth: 160 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {label}
      </Typography>
      {payload.map((p: any) => (
        <Box
          key={p.dataKey}
          sx={{ display: 'flex', justifyContent: 'space-between', gap: 3, mb: 0.25 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
            <Typography variant="caption">{p.name}</Typography>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {p.value != null ? Number(p.value).toFixed(2) : '—'}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

export default function FeedConversionRatio() {
  const [period, setPeriod] = useState<'weekly' | 'monthly'>('weekly');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedProcurement, setSelectedProcurement] = useState<number | ''>('');

  const { data, isLoading, error } = useFeedConversionRatio({
    date_from: dateFrom,
    date_to: dateTo,
    period,
    procurement_id: selectedProcurement || undefined,
  });

  // Unfiltered fetch to always have the full batch list for the selector
  const { data: allBatchesData } = useFeedConversionRatio({
    date_from: dateFrom,
    date_to: dateTo,
    period,
  });

  const summary = data?.summary;
  const timeline: any[] = useMemo(() => data?.timeline ?? [], [data]);
  const byProcurement: any[] = useMemo(() => data?.by_procurement ?? [], [data]);
  const allBatches: any[] = useMemo(() => allBatchesData?.by_procurement ?? [], [allBatchesData]);

  const uniqueBatches = useMemo(() => {
    const seen = new Set<number>();
    const result: { procurement_id: number; identifier: string }[] = [];
    for (const pt of timeline) {
      for (const b of pt.batches ?? []) {
        if (!seen.has(b.procurement_id)) {
          seen.add(b.procurement_id);
          result.push({ procurement_id: b.procurement_id, identifier: b.identifier });
        }
      }
    }
    return result;
  }, [timeline]);

  const fcrChartData = useMemo(
    () =>
      timeline.map((pt) => {
        const point: Record<string, any> = {
          label: pt.label,
          period: pt.period,
          overall: pt.overall?.fcr ?? null,
        };
        for (const b of pt.batches ?? []) {
          point[`batch_${b.procurement_id}`] = b.fcr ?? null;
        }
        return point;
      }),
    [timeline]
  );

  const costChartData = useMemo(
    () =>
      timeline.map((pt) => {
        const point: Record<string, any> = {
          label: pt.label,
          period: pt.period,
          overall: pt.overall?.cost_per_crate ?? null,
        };
        for (const b of pt.batches ?? []) {
          point[`batch_${b.procurement_id}`] = b.cost_per_crate ?? null;
        }
        return point;
      }),
    [timeline]
  );

  const showMultipleBatches = uniqueBatches.length > 1 && !selectedProcurement;

  const fmtFCR = (v: number | null | undefined) => (v != null ? v.toFixed(2) : null);
  const fmtCcy = (v: number | null | undefined) => (v != null ? `GHC ${v.toFixed(2)}` : null);
  const fmtKg = (v: number | null | undefined) => (v != null ? `${v.toLocaleString()} kg` : null);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load FCR data. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <GrainIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                background: 'linear-gradient(90deg, #ff9800, #ffcc02)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Feed Conversion Ratio
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Kg of feed consumed per dozen eggs produced — lower is more efficient
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              From
            </Typography>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                background: '#1e1e1e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 6,
                color: '#fff',
                padding: '6px 10px',
                fontSize: 13,
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              To
            </Typography>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                background: '#1e1e1e',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 6,
                color: '#fff',
                padding: '6px 10px',
                fontSize: 13,
              }}
            />
          </Box>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={period}
            onChange={(_, v) => v && setPeriod(v)}
          >
            <ToggleButton value="weekly" sx={{ px: 2, fontSize: 12 }}>
              Weekly
            </ToggleButton>
            <ToggleButton value="monthly" sx={{ px: 2, fontSize: 12 }}>
              Monthly
            </ToggleButton>
          </ToggleButtonGroup>

          {allBatches.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Batch</InputLabel>
              <Select
                label="Batch"
                value={selectedProcurement}
                onChange={(e) => setSelectedProcurement(e.target.value as number | '')}
              >
                <MenuItem value="">All batches</MenuItem>
                {allBatches.map((b) => (
                  <MenuItem key={b.procurement_id} value={b.procurement_id}>
                    {b.identifier}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2}>
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
              </Grid>
            ))
          : [
              {
                icon: <SpeedIcon sx={{ fontSize: 20 }} />,
                label: 'Overall FCR',
                value: fmtFCR(summary?.overall_fcr),
                sub: 'kg feed per dozen eggs',
                color: '#ff9800',
                lowerIsBetter: true,
              },
              {
                icon: <AttachMoneyIcon sx={{ fontSize: 20 }} />,
                label: 'Feed Cost / Crate',
                value: fmtCcy(summary?.cost_per_crate),
                sub: 'feed cost per 30-egg crate',
                color: '#42a5f5',
                lowerIsBetter: true,
              },
              {
                icon: <EggIcon sx={{ fontSize: 20 }} />,
                label: 'Feed Cost / Egg',
                value:
                  summary?.cost_per_egg != null ? `GHC ${summary.cost_per_egg.toFixed(4)}` : null,
                sub: 'feed cost per individual egg',
                color: '#66bb6a',
                lowerIsBetter: true,
              },
              {
                icon: <ScaleIcon sx={{ fontSize: 20 }} />,
                label: 'Feed / 100 Eggs',
                value: fmtKg(summary?.feed_kg_per_100_eggs),
                sub: 'kg consumed per 100 eggs',
                color: '#ab47bc',
                lowerIsBetter: true,
              },
            ].map((card) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
                <StatCard {...card} />
              </Grid>
            ))}
      </Grid>

      {/* Charts */}
      {isLoading ? (
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      ) : fcrChartData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No data for the selected period.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {/* FCR Trend */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <ChartSection
              title="FCR Trend"
              subtitle="Kg of feed per dozen eggs — track efficiency over time"
              height={300}
            >
              <LineChart data={fcrChartData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid {...GRID_STYLE} />
                <XAxis dataKey="label" tick={AXIS_STYLE} />
                <YAxis tick={AXIS_STYLE} domain={['auto', 'auto']} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine
                  y={3}
                  stroke="rgba(102,187,106,0.4)"
                  strokeDasharray="4 4"
                  label={{ value: 'Good (3.0)', fill: '#66bb6a', fontSize: 10 }}
                />
                {showMultipleBatches ? (
                  <>
                    <Line
                      type="monotone"
                      dataKey="overall"
                      name="Overall"
                      stroke="#ff9800"
                      strokeWidth={2.5}
                      dot={false}
                      strokeDasharray="5 3"
                    />
                    {uniqueBatches.map((b, i) => (
                      <Line
                        key={b.procurement_id}
                        type="monotone"
                        dataKey={`batch_${b.procurement_id}`}
                        name={b.identifier}
                        stroke={BATCH_COLORS[i % BATCH_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    ))}
                  </>
                ) : (
                  <Line
                    type="monotone"
                    dataKey={
                      showMultipleBatches
                        ? 'overall'
                        : uniqueBatches.length === 1
                          ? `batch_${uniqueBatches[0]?.procurement_id}`
                          : 'overall'
                    }
                    name={uniqueBatches.length === 1 ? uniqueBatches[0]?.identifier : 'Overall'}
                    stroke="#ff9800"
                    strokeWidth={2.5}
                    dot={{ fill: '#ff9800', r: 3 }}
                    connectNulls
                  />
                )}
              </LineChart>
            </ChartSection>
          </Grid>

          {/* Cost per Crate */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <ChartSection
              title="Feed Cost per Crate"
              subtitle="GHC — feed cost attributed per 30-egg crate"
              height={300}
            >
              <BarChart data={costChartData} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid {...GRID_STYLE} />
                <XAxis dataKey="label" tick={AXIS_STYLE} />
                <YAxis tick={AXIS_STYLE} />
                <Tooltip content={<CustomTooltip />} />
                {showMultipleBatches ? (
                  uniqueBatches.map((b, i) => (
                    <Bar
                      key={b.procurement_id}
                      dataKey={`batch_${b.procurement_id}`}
                      name={b.identifier}
                      fill={BATCH_COLORS[i % BATCH_COLORS.length]}
                      radius={[3, 3, 0, 0]}
                    />
                  ))
                ) : (
                  <Bar
                    dataKey={
                      uniqueBatches.length === 1
                        ? `batch_${uniqueBatches[0]?.procurement_id}`
                        : 'overall'
                    }
                    name={uniqueBatches.length === 1 ? uniqueBatches[0]?.identifier : 'Overall'}
                    fill="#42a5f5"
                    radius={[3, 3, 0, 0]}
                  />
                )}
              </BarChart>
            </ChartSection>
          </Grid>
        </Grid>
      )}

      {/* Batch Summary Table */}
      {!isLoading && byProcurement.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Batch Breakdown
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    'Batch',
                    'Total Feed (kg)',
                    'Feed Cost',
                    'Eggs',
                    'Crates',
                    'FCR',
                    'Cost/Crate',
                    'Cost/Egg',
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 600 }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {byProcurement.map((b, i) => (
                  <TableRow key={b.procurement_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: BATCH_COLORS[i % BATCH_COLORS.length],
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {b.identifier}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {b.total_feed_kg?.toLocaleString() ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {b.total_feed_cost != null
                          ? `GHC ${b.total_feed_cost.toLocaleString('en', { minimumFractionDigits: 2 })}`
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {b.total_eggs?.toLocaleString() ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {b.total_crates?.toLocaleString() ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {b.overall_fcr != null ? (
                        <Chip
                          label={b.overall_fcr.toFixed(2)}
                          size="small"
                          sx={{
                            bgcolor:
                              b.overall_fcr <= 3
                                ? 'rgba(102,187,106,0.15)'
                                : b.overall_fcr <= 4
                                  ? 'rgba(255,152,0,0.15)'
                                  : 'rgba(244,67,54,0.15)',
                            color:
                              b.overall_fcr <= 3
                                ? '#66bb6a'
                                : b.overall_fcr <= 4
                                  ? '#ff9800'
                                  : '#f44336',
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {b.cost_per_crate != null ? `GHC ${b.cost_per_crate.toFixed(2)}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {b.cost_per_egg != null ? `GHC ${b.cost_per_egg.toFixed(4)}` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Totals row */}
                {byProcurement.length > 1 && summary && (
                  <TableRow
                    sx={{
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                      bgcolor: 'rgba(255,152,0,0.04)',
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700 }}>Total / Average</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {summary.total_feed_kg?.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {summary.total_feed_cost != null
                        ? `GHC ${summary.total_feed_cost.toLocaleString('en', { minimumFractionDigits: 2 })}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {summary.total_eggs?.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {summary.total_crates?.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {summary.overall_fcr != null && (
                        <Chip
                          label={summary.overall_fcr.toFixed(2)}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,152,0,0.15)',
                            color: '#ff9800',
                            fontWeight: 700,
                            fontSize: 12,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {summary.cost_per_crate != null
                        ? `GHC ${summary.cost_per_crate.toFixed(2)}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>
                      {summary.cost_per_egg != null
                        ? `GHC ${summary.cost_per_egg.toFixed(4)}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
